import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import clsx from "clsx";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import useLocalStorage from "@/hooks/useLocalStorage";
import { appendPuzzleViewToPath } from "@/helpers/puzzle-view-url";
import { buildPuzzleListingNavigationUrl } from "@/helpers/withCurrentSearchParams";
import {
    buildPuzzleListingPathFromNextItem,
    fetchNextPlayableProperty,
    type PuzzleGuessedPropertyResult,
} from "@/helpers/puzzleNextPlayableProperty";
import { useUser } from "@/context/UserContext";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import {
    createPlayablePropertyComment,
    getPlayablePropertyComments,
    getPuzzleCommunityStats,
    getPuzzleScoreboard,
} from "@/services/PropertyService";
import toast from "react-hot-toast";
import { buildPuzzleShareDetails } from "@/helpers/puzzleShare";
import { useShareScoreSheet } from "@/components/common/share-score-sheet/share-score-sheet-context";
import { CommentsSection } from "./ScoreboardFeed/comments-section";
import ScoreboardActionRow from "./ScoreboardFeed/scoreboard-action-row";
import StickyCommentInputBar from "./StickyCommentInputBar";
import type { GuessCell } from "./ScoreboardFeed/guess-grid";
import type { UserScoreRowData } from "./ScoreboardFeed/user-score-row";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { logger } from "@/services/LoggerService";
import { convertStatsToGuessCell } from "@/helpers/puzzleGuessCells";
import {
    PUZZLE_GUEST_SCORES_MIGRATING_KEY,
} from "@/constants/puzzle-storage.constants";
import {
    MIXPANEL_REGISTER_LOCATION_COMMENT_GATE,
    MIXPANEL_REGISTER_LOCATION_LIKE_GATE_COMMENT,
    type MixpanelSignupPromptSource,
} from "@/constants/mixpanel.constants";
import { COMMENT_LIKE_SIGN_IN_DESCRIPTION } from "@/constants/like.constants";
import GuestAuthPromptModal from "@/components/common/GuestAuthPromptModal";
import useScoreboardSSE from "@/hooks/useScoreboardSSE";

/** Default delay before auto-opening the auth modal when the guest opens the scoreboard (no streak-dismiss gate). */
const SCOREBOARD_AUTH_MODAL_DEFAULT_DELAY_MS = 1_500;

function shareGuessesFromScoreBadge(scoreBadgeGuesses: GuessCell[][] | undefined): string[] {
    if (!scoreBadgeGuesses?.length) {
        return [];
    }
    return scoreBadgeGuesses.map((row) => row.map((cell) => cell.digit).join("").padStart(8, "0"));
}

export type ScoreboardViewLocation = "Puzzle Drawer" | "Auto";

export interface ScoreboardProps {
    isPropertyGuessed?: boolean;
    playablePropertyId?: string;
    isActive?: boolean;
    actualPrice?: number;
    propertyId?: string;
    score?: number;
    shortLink?: string;
    propertyShareLabel?: string;
    propertyPlayDate?: string;
    registeredStreak?: number | null;
    scoreBadgeGuesses?: GuessCell[][];
    /** Source of scoreboard visibility for analytics; defaults to tab UI. */
    viewLocation?: ScoreboardViewLocation;
    /** When true, hides the scoreboard auth overlay so it does not stack with the streak interstitial. */
    isStreakModalOpen?: boolean;
    /** Guest numeric guesses for puzzle scoreboard GET (provisional `currentUserScore`); omit for registered users. */
    guestGuessesForScoreboard?: number[];
    /** When > 0, guest must not see auto-opened scoreboard auth modal before this unix ms (set in Puzzle when streak closes). */
    guestScoreboardAuthNotBeforeMs?: number;
    /** Clears the post-streak auth delay in Puzzle after the delayed auto-open runs. */
    onGuestScoreboardAuthDelayConsumed?: () => void;
    /** Persist pending guesses + Auth0 signup (from Puzzle; same as streak modal Save). */
    onSaveGuestScore?: () => void;
    /** Puzzle route version key (e.g. v5) for guest next-property lookup. */
    puzzleVersion?: string;
    /** Switch to guesses tab before navigating to the next listing (no #scoreboard). */
    onBeforeNavigateToNextPuzzle?: () => void;
}

/**
 * Scoreboard component displays community performance for the Puzzle game mode,
 * including example user scores and guess histories, and manages auth-gated
 * interactions when the property has or has not been guessed.
 *
 * @component
 * @param {ScoreboardProps} props - Props for the Scoreboard component.
 * @param {boolean} [props.isPropertyGuessed] - Whether the current puzzle property has been successfully guessed.
 * @param {string} [props.playablePropertyId] - The playable property ID to fetch scoreboard data for.
 * @param {boolean} [props.isActive] - Whether the scoreboard is currently active/visible to trigger data fetching.
 * @param {number} [props.actualPrice] - The actual price of the property for color-coding guess tiles.
 * @param {string} [props.propertyId] - The property ID from the property object.
 * @returns {JSX.Element} The rendered Puzzle scoreboard UI.
 */
export default function ScoreboardFeed({
    isPropertyGuessed,
    playablePropertyId,
    isActive = false,
    actualPrice,
    propertyId,
    score,
    shortLink,
    propertyShareLabel,
    propertyPlayDate,
    registeredStreak,
    scoreBadgeGuesses,
    viewLocation = "Puzzle Drawer",
    isStreakModalOpen = false,
    guestGuessesForScoreboard,
    guestScoreboardAuthNotBeforeMs = 0,
    onGuestScoreboardAuthDelayConsumed,
    onSaveGuestScore,
    puzzleVersion = "puzzle",
    onBeforeNavigateToNextPuzzle,
}: ScoreboardProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const puzzleFeedSlc = searchParams.get("slc");
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [guestAuthSource, setGuestAuthSource] = useState<MixpanelSignupPromptSource>("scoreboard");
    const [guestAuthRegisterLocation, setGuestAuthRegisterLocation] = useState<string>("Scoreboard");
    const [guestAuthDescription, setGuestAuthDescription] = useState<string>(
        "Sign in to view community stats and see what other players guessed",
    );
    const [isNextPuzzleBusy, setIsNextPuzzleBusy] = useState(false);
    const [isMigratingGuestScores, setIsMigratingGuestScores] = useState<boolean>(() => {
        try {
            return sessionStorage.getItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY) === "true";
        } catch {
            return false;
        }
    });
    const [guessedPropertiesLocal] = useLocalStorage<string[]>("guessed_properties", []);
    const [guessedPropertyResultsLocal] = useLocalStorage<PuzzleGuessedPropertyResult[]>(
        "guessed_property_results",
        [],
    );
    /** True after a post-streak delayed open was scheduled or fired; suppresses the default 1.5s auto-open. */
    const postStreakGuestAuthTimerDoneRef = useRef<boolean>(false);
    const [communityStats, setCommunityStats] = useState<{
        averageFirstGuess: number | null;
        averageScore: number | null;
        highestGuess: number | null;
        lowestGuess: number | null;
        totalUsers: number;
    } | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { openShareScoreSheet } = useShareScoreSheet();
    const queryClient = useQueryClient();
    useScoreboardSSE(
        playablePropertyId ? String(playablePropertyId) : null,
        Boolean(playablePropertyId && isActive),
    );
    const guestAuthRedirectPath = useMemo(
        () => appendPuzzleViewToPath(`${location.pathname}${location.search}`, "scoreboard"),
        [location.pathname, location.search],
    );
    const { handleGoogle, handleEmail } = useOneTapAuth({
        redirectPath: guestAuthRedirectPath,
        version: "puzzle",
        location: guestAuthRegisterLocation, // Register event
    });
    /** @param registerLocation Register event `location` (useOneTapAuth) @param source Signup Prompt `source` (GuestAuthPromptModal) @param description Modal body copy */
    const openGuestAuth = useCallback((
        registerLocation: string,
        source: MixpanelSignupPromptSource,
        description: string,
    ) => {
        setGuestAuthRegisterLocation(registerLocation);
        setGuestAuthSource(source);
        setGuestAuthDescription(description);
        setShowAuthModal(true);
    }, []);

    const isUserAuthenticated = isAuthenticated || userState?.is_created;

    const handleNextPuzzleFromScoreboard = useCallback(async () => {
        sendMixpanelEvent("Next Puzzle Tap", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
        });
        setIsNextPuzzleBusy(true);
        try {
            const feedUserId =
                isUserAuthenticated && userState?.id ? String(userState.id) : undefined;
            const nextProperty = await fetchNextPlayableProperty({
                userId: feedUserId,
                requestingUserId: feedUserId,
                slc: puzzleFeedSlc,
                queryClient,
                currentPlayablePropertyId: playablePropertyId ?? null,
                currentPropertyId: propertyId,
                guessedPropertyIds: guessedPropertiesLocal ?? [],
                guessedPropertyResults: guessedPropertyResultsLocal ?? [],
                versionStored: puzzleVersion,
            });
            if (!nextProperty) {
                toast.error("No more puzzles available right now.");
                return;
            }
            const listingPath = buildPuzzleListingPathFromNextItem(nextProperty);
            if (!listingPath) {
                toast.error("Unable to open the next puzzle.");
                return;
            }
            onBeforeNavigateToNextPuzzle?.();
            navigate(buildPuzzleListingNavigationUrl(listingPath));
        } catch (error) {
            logger.error("Next puzzle navigation failed", error, {
                context: "ScoreboardFeed.handleNextPuzzleFromScoreboard",
                playablePropertyId,
            });
            toast.error("Unable to load the next puzzle. Please try again.");
        } finally {
            setIsNextPuzzleBusy(false);
        }
    }, [
        guessedPropertiesLocal,
        guessedPropertyResultsLocal,
        isUserAuthenticated,
        navigate,
        onBeforeNavigateToNextPuzzle,
        playablePropertyId,
        propertyId,
        puzzleFeedSlc,
        puzzleVersion,
        queryClient,
        sendMixpanelEvent,
        userState?.emailAddress,
        userState?.id,
        userState?.nickname,
    ]);
    const requestingUserKey = userState?.id ? String(userState.id) : "anonymous";
    const scoreboardViewKeyRef = useRef<string | null>(null);
    const scoreboardScrollContainerRef = useRef<HTMLDivElement>(null);
    const commentsQueryKey = useMemo(
        () => ["playable-property-comments", playablePropertyId ?? null, userState?.id ?? null] as const,
        [playablePropertyId, userState?.id],
    );

    useEffect(() => {
        if (!isActive || !playablePropertyId) return;
        const key = `${playablePropertyId}:${viewLocation}`;
        if (scoreboardViewKeyRef.current === key) return;
        scoreboardViewKeyRef.current = key;

        sendMixpanelEvent("Scoreboard View", {
            location: viewLocation,
            pageName: "Puzzle Scoreboard",
            pageUrl: window.location.href,
            isAuthenticated: !!userState?.id,
            visitorStatus: userState?.id ? null : "new-guest",
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            propertyUrl: playablePropertyId ? `${window.location.origin}/puzzle/${playablePropertyId}` : null,
        });
    }, [isActive, playablePropertyId, sendMixpanelEvent, userState, viewLocation]);

    const handleBlurredClick = () => {
        if (!isUserAuthenticated) {
            openGuestAuth(
                "Scoreboard", // location
                "scoreboard", // source
                "Sign in to view community stats and see what other players guessed", // description
            );
        }
    };

    const handleSaveGuestScoreClick = useCallback(() => {
        if (isUserAuthenticated) {
            return;
        }
        sendMixpanelEvent("Scoreboard Save Clicked", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
        });
        postStreakGuestAuthTimerDoneRef.current = true;
        setShowAuthModal(false);
        onSaveGuestScore?.();
    }, [isUserAuthenticated, onSaveGuestScore, sendMixpanelEvent, userState?.emailAddress, userState?.nickname]);

    useEffect(() => {
        const notBefore = guestScoreboardAuthNotBeforeMs ?? 0;
        if (notBefore > Date.now()) {
            postStreakGuestAuthTimerDoneRef.current = false;
        }
    }, [guestScoreboardAuthNotBeforeMs]);

    useEffect(() => {
        postStreakGuestAuthTimerDoneRef.current = false;
    }, [playablePropertyId]);

    useEffect(() => {
        if (isStreakModalOpen) {
            postStreakGuestAuthTimerDoneRef.current = false;
        }
    }, [isStreakModalOpen]);

    const shouldDisplayAuthModal = useMemo(() => {
        return showAuthModal && !isStreakModalOpen;
    }, [isStreakModalOpen, showAuthModal]);

    const {
        data: commentsResponse,
        isLoading: isLoadingComments,
        isFetching: isFetchingComments,
        isError: isCommentsError,
        refetch: refetchComments,
    } = useQuery({
        queryKey: ["playable-property-comments", playablePropertyId ?? null, userState?.id ?? null],
        queryFn: () =>
            getPlayablePropertyComments(String(playablePropertyId), {
                include: ["guesses", "replies"],
                requestingUserId: userState?.id ? String(userState.id) : undefined,
            }),
        enabled: Boolean(playablePropertyId && isActive),
        retry: false,
        staleTime: 0,
        refetchOnMount: "always",
    });

    useEffect(() => {
        if (!playablePropertyId || !isActive) {
            return;
        }
        void refetchComments();
    }, [isActive, playablePropertyId, refetchComments]);

    const comments = useMemo(() => commentsResponse?.data ?? [], [commentsResponse?.data]);
    const totalGuesses = commentsResponse?.guess_count || 0;
    const totalReplies = commentsResponse?.replies_count || 0;
    const isLoading = isLoadingComments || isFetchingComments;
    const fetchError = isCommentsError ? "Failed to load comments data" : null;

    const puzzleScoreboardQueryEnabled =
        Boolean(playablePropertyId && isActive && isPropertyGuessed && actualPrice) &&
        Boolean(isUserAuthenticated ? userState?.id : guestGuessesForScoreboard?.length);

    const clearGuestScoresMigratingBanner = useCallback(() => {
        setIsMigratingGuestScores(false);
        try {
            sessionStorage.removeItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY);
        } catch {
            /* ignore */
        }
    }, []);

    const {
        data: puzzleScoreboardBody,
        isSuccess: isPuzzleScoreboardSuccess,
        isError: isPuzzleScoreboardError,
        isFetched: isPuzzleScoreboardFetched,
        isFetching: isPuzzleScoreboardFetching,
    } = useQuery({
        queryKey: [
            "puzzle-scoreboard-current-user",
            playablePropertyId ?? null,
            isUserAuthenticated,
            userState?.id ?? null,
            guestGuessesForScoreboard ?? null,
            guestGuessesForScoreboard?.length ?? 0,
        ],
        queryFn: async () => {
            const res = await getPuzzleScoreboard({
                playablePropertyId: String(playablePropertyId),
                page: 1,
                limit: 10,
                currentUserId: isUserAuthenticated && userState?.id ? String(userState.id) : undefined,
                guestGuesses:
                    !isUserAuthenticated && guestGuessesForScoreboard && guestGuessesForScoreboard.length > 0
                        ? guestGuessesForScoreboard
                        : undefined,
            });
            return res.data;
        },
        enabled: puzzleScoreboardQueryEnabled,
        retry: false,
        gcTime: 0,
    });

    useEffect(() => {
        if (!isMigratingGuestScores) {
            return;
        }
        let isSessionMigrating = false;
        try {
            isSessionMigrating = sessionStorage.getItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY) === "true";
        } catch {
            isSessionMigrating = false;
        }
        if (!isSessionMigrating) {
            setIsMigratingGuestScores(false);
            return;
        }
        if (!isUserAuthenticated) {
            return;
        }
        if (!puzzleScoreboardQueryEnabled) {
            clearGuestScoresMigratingBanner();
            return;
        }
        const hasScoreboardSettled =
            isPuzzleScoreboardFetched &&
            !isPuzzleScoreboardFetching &&
            (isPuzzleScoreboardSuccess || isPuzzleScoreboardError);
        if (hasScoreboardSettled) {
            clearGuestScoresMigratingBanner();
        }
    }, [
        clearGuestScoresMigratingBanner,
        isMigratingGuestScores,
        isPuzzleScoreboardError,
        isPuzzleScoreboardFetched,
        isPuzzleScoreboardFetching,
        isPuzzleScoreboardSuccess,
        isUserAuthenticated,
        playablePropertyId,
        puzzleScoreboardQueryEnabled,
    ]);

    /** True only when this playable property has a completed guess on record (not in-progress typing). */
    const hasGuessedCurrentPlayableProperty = useMemo(() => {
        if (isPropertyGuessed) {
            return true;
        }
        if (!playablePropertyId) {
            return false;
        }
        const playableIdStr = String(playablePropertyId);
        const isInGuessedProperties = (guessedPropertiesLocal ?? []).some((id) => String(id) === playableIdStr);
        const hasGuessedResult = (guessedPropertyResultsLocal ?? []).some(
            (result) =>
                String(result.playablePropertyId) === playableIdStr && result.version === puzzleVersion,
        );
        if (isInGuessedProperties && hasGuessedResult) {
            return true;
        }
        if (isUserAuthenticated) {
            const apiGuesses = puzzleScoreboardBody?.currentUserScore?.guesses;
            return Array.isArray(apiGuesses) && apiGuesses.length > 0;
        }
        return false;
    }, [
        guessedPropertiesLocal,
        guessedPropertyResultsLocal,
        isPropertyGuessed,
        isUserAuthenticated,
        playablePropertyId,
        puzzleScoreboardBody?.currentUserScore?.guesses,
        puzzleVersion,
    ]);

    const shareGuesses = useMemo(() => {
        const apiGuesses = puzzleScoreboardBody?.currentUserScore?.guesses;
        if (Array.isArray(apiGuesses) && apiGuesses.length > 0) {
            return apiGuesses.map((guess) => String(guess).padStart(8, "0"));
        }
        if (guestGuessesForScoreboard && guestGuessesForScoreboard.length > 0) {
            return guestGuessesForScoreboard.map((guess) => String(guess).padStart(8, "0"));
        }
        return shareGuessesFromScoreBadge(scoreBadgeGuesses);
    }, [guestGuessesForScoreboard, puzzleScoreboardBody?.currentUserScore?.guesses, scoreBadgeGuesses]);

    const canShareScore = useMemo(() => {
        return Boolean(hasGuessedCurrentPlayableProperty && actualPrice && shortLink && shareGuesses.length > 0);
    }, [actualPrice, hasGuessedCurrentPlayableProperty, shareGuesses.length, shortLink]);

    const handleShareScoreFromScoreboard = useCallback(() => {
        if (!canShareScore) {
            toast.error("Unable to share score right now.");
            return;
        }
        // `canShareScore` implies these are defined; keep explicit narrowing for TS.
        if (!actualPrice || !shortLink) {
            toast.error("Unable to share score right now.");
            return;
        }
        const shareDetails = buildPuzzleShareDetails({
            guesses: shareGuesses,
            actualPrice,
            playDate: propertyPlayDate,
            shortLink,
            propertyLabel: propertyShareLabel,
        });
        const propertyUrl = `${window.location.origin}/puzzle/${shortLink}`;
        openShareScoreSheet({
            shareTextTitle: shareDetails.shareTextTitle,
            shareTextBody: shareDetails.shareTextBody,
            shareUrl: shareDetails.shareUrl,
            streak: isUserAuthenticated ? registeredStreak ?? null : null,
            location: "Scoreboard",
            pageName: document.title,
            propertyUrl,
            userScore: score ?? 0,
        });
    }, [
        actualPrice,
        canShareScore,
        isUserAuthenticated,
        openShareScoreSheet,
        propertyPlayDate,
        propertyShareLabel,
        registeredStreak,
        score,
        shareGuesses,
        shortLink,
    ]);

    const updateCommentsCache = useCallback(
        (
            updater: (
                prev: Awaited<ReturnType<typeof getPlayablePropertyComments>>,
            ) => Awaited<ReturnType<typeof getPlayablePropertyComments>>,
        ) => {
            queryClient.setQueryData(
                commentsQueryKey,
                (prev: Awaited<ReturnType<typeof getPlayablePropertyComments>> | undefined) => {
                    const base =
                        prev ??
                        ({
                            data: [],
                            guess_count: 0,
                            replies_count: 0,
                        } as Awaited<ReturnType<typeof getPlayablePropertyComments>>);
                    return updater(base);
                },
            );
        },
        [commentsQueryKey, queryClient],
    );

    const invalidatePuzzleStats = useCallback(() => {
        if (!playablePropertyId) return;
        const playablePropertyIdStr = String(playablePropertyId);
        void queryClient.invalidateQueries({ queryKey: ["property-puzzle-stats", playablePropertyIdStr] });
    }, [playablePropertyId, queryClient]);

    const invalidatePuzzleFeedCommentPreview = useCallback(() => {
        if (!playablePropertyId) return;
        const playablePropertyIdStr = String(playablePropertyId);
        void queryClient.invalidateQueries({
            queryKey: ["puzzle-feed-comments-preview", playablePropertyIdStr, requestingUserKey],
            exact: true,
        });
        // Invalidate legacy (viewer-agnostic) entries to prevent cross-auth overwrite/leaks.
        void queryClient.invalidateQueries({
            queryKey: ["puzzle-feed-comments-preview", playablePropertyIdStr],
            exact: true,
        });
    }, [playablePropertyId, queryClient, requestingUserKey]);

    /**
     * Sync after a successful create. Must not reject — refresh/invalidate failures
     * should not surface as “couldn’t post” when the comment was already created.
     */
    const syncCommentsAfterPost = useCallback(async () => {
        let refreshedComments: Awaited<ReturnType<typeof getPlayablePropertyComments>> | null = null;
        try {
            const refreshed = await refetchComments();
            refreshedComments = (refreshed.data as Awaited<ReturnType<typeof getPlayablePropertyComments>>) ?? null;
        } catch (error) {
            logger.error("Comments refresh after post failed", error, { playablePropertyId });
        }
        try {
            if (playablePropertyId && refreshedComments) {
                queryClient.setQueryData(
                    ["puzzle-feed-comments-preview", String(playablePropertyId), requestingUserKey],
                    refreshedComments,
                );
            }
            invalidatePuzzleStats();
            invalidatePuzzleFeedCommentPreview();
            if (playablePropertyId) {
                void queryClient.invalidateQueries({
                    queryKey: ["puzzle-scoreboard-current-user", String(playablePropertyId)],
                });
            }
        } catch (error) {
            logger.error("Puzzle stats invalidation after post failed", error, { playablePropertyId });
        }
    }, [
        invalidatePuzzleFeedCommentPreview,
        invalidatePuzzleStats,
        playablePropertyId,
        queryClient,
        refetchComments,
        requestingUserKey,
    ]);

    const applyFollowStateToCommentTree = useCallback((nodes: any[], targetUserId: string, nextIsFollowing: boolean): any[] => {
        const updateNode = (node: any): any => {
            if (!node || typeof node !== "object") return node;
            const nodeUserId = node.userId ? String(node.userId) : null;
            const nextNode =
                nodeUserId && nodeUserId === targetUserId ? { ...node, isFollowing: nextIsFollowing } : node;
            if (!Array.isArray(nextNode.replies)) return nextNode;
            return {
                ...nextNode,
                replies: nextNode.replies.map(updateNode),
            };
        };
        return nodes.map(updateNode);
    }, []);

    const insertReplyLocally = useCallback(
        (parentCommentId: string, content: string, createdCommentId?: string) => {
            const nowIso = new Date().toISOString();
            const currentUserId = userState?.id ? String(userState.id) : "";
            const currentNickname = userState?.nickname || "You";
            const currentPhoto = userState?.profilePhotoUrl || null;
            const currentIsAgent = userState?.type === "agent";

            const appendReply = (nodes: any[]): any[] =>
                nodes.map((node) => {
                    if (String(node?.id ?? "") === String(parentCommentId)) {
                        const nextReplies = Array.isArray(node?.replies) ? node.replies : [];
                        return {
                            ...node,
                            replies: [
                                ...nextReplies,
                                {
                                    id: createdCommentId ?? `tmp-reply-${Date.now()}`,
                                    userId: currentUserId,
                                    username: currentNickname,
                                    isAgent: currentIsAgent,
                                    isFollowing: false,
                                    profilePhoto: currentPhoto,
                                    profilePrivate: false,
                                    showScoreLeaderboard: true,
                                    createdAt: nowIso,
                                    body: content,
                                    guesses: [],
                                    replies: [],
                                },
                            ],
                        };
                    }
                    if (Array.isArray(node?.replies) && node.replies.length > 0) {
                        return {
                            ...node,
                            replies: appendReply(node.replies),
                        };
                    }
                    return node;
                });

            updateCommentsCache((prev) => ({
                ...prev,
                data: appendReply(prev.data || []),
                replies_count: Number(prev.replies_count ?? 0) + 1,
            }));
        },
        [updateCommentsCache, userState],
    );

    const insertTopLevelCommentLocally = useCallback(
        (content: string, createdCommentId?: string) => {
            const nowIso = new Date().toISOString();
            const currentUserId = userState?.id ? String(userState.id) : "";
            const currentNickname = userState?.nickname || "You";
            const currentPhoto = userState?.profilePhotoUrl || null;
            const currentIsAgent = userState?.type === "agent";
            const currentUserGuesses = puzzleScoreboardBody?.currentUserScore?.guesses ?? [];

            updateCommentsCache((prev) => {
                const rows = prev.data || [];
                // If this user already has a score-only row, convert it into text+score.
                const idx = rows.findIndex(
                    (entry) => String(entry?.userId ?? "") === currentUserId && (!entry?.body || entry?.body === null),
                );
                if (idx >= 0) {
                    const next = [...rows];
                    const existing = next[idx];
                    const mergedGuesses =
                        Array.isArray(existing?.guesses) && existing.guesses.length > 0
                            ? existing.guesses
                            : currentUserGuesses;
                    next[idx] = {
                        ...existing,
                        id: createdCommentId ?? existing?.id ?? `tmp-comment-${Date.now()}`,
                        body: content,
                        createdAt: nowIso,
                        guesses: mergedGuesses,
                    };
                    return {
                        ...prev,
                        data: next,
                        replies_count: Number(prev.replies_count ?? 0) + 1,
                    };
                }

                return {
                    ...prev,
                    data: [
                        {
                            id: createdCommentId ?? `tmp-comment-${Date.now()}`,
                            userId: currentUserId,
                            username: currentNickname,
                            isAgent: currentIsAgent,
                            isFollowing: false,
                            profilePhoto: currentPhoto,
                            profilePrivate: false,
                            showScoreLeaderboard: true,
                            createdAt: nowIso,
                            body: content,
                            guesses: currentUserGuesses,
                            replies: [],
                        },
                        ...rows,
                    ],
                    replies_count: Number(prev.replies_count ?? 0) + 1,
                };
            });
        },
        [puzzleScoreboardBody?.currentUserScore?.guesses, updateCommentsCache, userState],
    );

    // iOS Safari can restore this view from bfcache; refresh scoreboard comments on resume.
    useEffect(() => {
        if (!playablePropertyId || !isActive) return;

        const refreshScoreboard = () => {
            void refetchComments();
        };

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                refreshScoreboard();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                refreshScoreboard();
            }
        };

        window.addEventListener("pageshow", handlePageShow);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", refreshScoreboard);

        return () => {
            window.removeEventListener("pageshow", handlePageShow);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", refreshScoreboard);
        };
    }, [playablePropertyId, isActive, refetchComments]);


    // Fetch community stats when playablePropertyId is available
    useEffect(() => {
        const fetchCommunityStats = async () => {
            if (!playablePropertyId || !isActive) return;

            setIsLoadingStats(true);
            try {
                const data = await getPuzzleCommunityStats(String(playablePropertyId));
                setCommunityStats(data);
            } catch (error) {
                toast.error("Unable to load community stats. Please try again.");
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchCommunityStats();
    }, [playablePropertyId, isActive]);

    // Auto-open auth modal for guests: wait until Puzzle post-streak `notBefore`, else default delay.
    useEffect(() => {
        if (isUserAuthenticated || !isActive || isLoading || isStreakModalOpen) {
            return;
        }
        const notBefore = guestScoreboardAuthNotBeforeMs ?? 0;
        if (notBefore > Date.now()) {
            const delayMs = notBefore - Date.now();
            const timeoutId = window.setTimeout(() => {
                postStreakGuestAuthTimerDoneRef.current = true;
                openGuestAuth(
                    "Scoreboard", // location
                    "scoreboard", // source
                    "Sign in to view community stats and see what other players guessed", // description
                );
                onGuestScoreboardAuthDelayConsumed?.();
            }, delayMs);
            return () => window.clearTimeout(timeoutId);
        }
        if (notBefore > 0) {
            onGuestScoreboardAuthDelayConsumed?.();
        }
        if (notBefore === 0 && !postStreakGuestAuthTimerDoneRef.current) {
            const timeoutId = window.setTimeout(() => {
                postStreakGuestAuthTimerDoneRef.current = true;
                openGuestAuth(
                    "Scoreboard", // location
                    "scoreboard", // source
                    "Sign in to view community stats and see what other players guessed", // description
                );
            }, SCOREBOARD_AUTH_MODAL_DEFAULT_DELAY_MS);
            return () => window.clearTimeout(timeoutId);
        }
        return undefined;
    }, [
        guestScoreboardAuthNotBeforeMs,
        isActive,
        isLoading,
        isStreakModalOpen,
        isUserAuthenticated,
        onGuestScoreboardAuthDelayConsumed,
        openGuestAuth,
    ]);

    const userScoreRow = useMemo<UserScoreRowData | null>(() => {
        const hasSessionGuesses = Boolean(scoreBadgeGuesses?.length);
        if ((!isPropertyGuessed && !hasSessionGuesses) || !actualPrice) {
            return null;
        }
        const apiRow = puzzleScoreboardBody?.currentUserScore;
        const apiCells = apiRow?.guesses?.length
            ? convertStatsToGuessCell(apiRow.guesses, actualPrice)
            : [];
        const localCells = scoreBadgeGuesses?.length ? scoreBadgeGuesses : [];
        // Prefer in-session guesses; API can lag after Next Puzzle before submitGuess settles.
        const isUsingLocalCells = localCells.length > 0;
        const guessCells = isUsingLocalCells ? localCells : apiCells;
        if (guessCells.length === 0) return null;
        const lastRow = guessCells[guessCells.length - 1];
        const isSolved = lastRow?.every((cell) => cell.status === "correct") ?? false;
        const nick = apiRow?.nickname ?? userState?.nickname ?? undefined;
        return {
            guesses: guessCells,
            isSolved,
            isGuest: apiRow?.isGuest ?? !isUserAuthenticated,
            lastGuessAt: isUsingLocalCells ? null : (apiRow?.lastGuessAt ?? null),
            nickname: nick || undefined,
            initials: nick
                ? nick[0].toUpperCase()
                : userState?.nickname
                  ? userState.nickname[0].toUpperCase()
                  : "Y",
            profilePhoto: userState?.profilePhotoUrl || undefined,
        };
    }, [
        actualPrice,
        isPropertyGuessed,
        isUserAuthenticated,
        puzzleScoreboardBody,
        scoreBadgeGuesses,
        userState?.nickname,
        userState?.profilePhotoUrl,
    ]);
    const currentUserHasTopLevelComment = useMemo(() => {
        if (!isUserAuthenticated || !userState?.id) {
            return false;
        }
        const currentUserId = String(userState.id);
        return comments.some(
            (entry) =>
                String(entry?.userId ?? "") === currentUserId &&
                Boolean(entry?.body && String(entry.body).trim().length > 0),
        );
    }, [comments, isUserAuthenticated, userState?.id]);

    const shouldPinUserScoreRow = Boolean(userScoreRow && !currentUserHasTopLevelComment);

    const filteredComments = useMemo(() => {
        if (!shouldPinUserScoreRow || !userState?.id) {
            return comments;
        }
        const currentUserId = String(userState.id);
        return comments.filter((entry) => {
            const entryUserId = entry?.userId ? String(entry.userId) : "";
            const hasText = Boolean(entry?.body && String(entry.body).trim().length > 0);
            if (entryUserId !== currentUserId) {
                return true;
            }
            return hasText;
        });
    }, [comments, shouldPinUserScoreRow, userState?.id]);

    /**
     * Renders skeleton loader for scoreboard items during initial load.
     */
    const renderSkeleton = () => (
        <>
            {[1, 2, 3].map((i) => (
                <div key={`skeleton-${i}`} className="tw-flex tw-gap-2.5 tw-py-3 tw-border-b tw-border-[#3a3a5c]">
                    {/* Avatar skeleton */}
                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#2E2E2E] tw-flex-shrink-0 tw-animate-pulse" />

                    <div className="tw-flex-1 tw-min-w-0">
                        {/* Username and time row */}
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                            <div className="tw-h-4 tw-bg-[#2E2E2E] tw-rounded tw-w-20 tw-animate-pulse" />
                            <div className="tw-h-3 tw-bg-[#2E2E2E] tw-rounded tw-w-12 tw-ml-auto tw-animate-pulse" />
                        </div>

                        {/* Score badge skeleton */}
                        <div className="tw-mb-1.5">
                            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-1 tw-rounded-lg tw-bg-[#2E2E2E] tw-animate-pulse">
                                <div className="tw-flex tw-flex-col tw-gap-0.5">
                                    <div className="tw-flex tw-gap-0.5">
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                                            <div key={j} className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-[#3E3E3E]" />
                                        ))}
                                    </div>
                                    <div className="tw-flex tw-gap-0.5">
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                                            <div key={j} className="tw-w-1.5 tw-h-1.5 tw-rounded-sm tw-bg-[#3E3E3E]" />
                                        ))}
                                    </div>
                                </div>
                                <div className="tw-h-4 tw-bg-[#3E3E3E] tw-rounded tw-w-16" />
                            </div>
                        </div>

                        {/* Comment text skeleton (sometimes) */}
                        {i % 2 === 0 && (
                            <div className="tw-space-y-1 tw-mb-1.5">
                                <div className="tw-h-4 tw-bg-[#2E2E2E] tw-rounded tw-w-full tw-animate-pulse" />
                                <div className="tw-h-4 tw-bg-[#2E2E2E] tw-rounded tw-w-3/4 tw-animate-pulse" />
                            </div>
                        )}

                        {/* Reply button skeleton */}
                        <div className="tw-flex tw-gap-4 tw-mt-1.5">
                            <div className="tw-h-3 tw-bg-[#2E2E2E] tw-rounded tw-w-12 tw-animate-pulse" />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );

    return (
        <div className="tw-relative tw-h-full tw-flex tw-flex-col tw-bg-[#1a1a2e]">
            <style>{`
                .scoreboard-container::-webkit-scrollbar {
                    display: none;
                }
                .scoreboard-container {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                
            `}</style>

            <GuestAuthPromptModal
                show={shouldDisplayAuthModal}
                onClose={() => {
                    postStreakGuestAuthTimerDoneRef.current = true;
                    setShowAuthModal(false);
                }}
                onGoogle={handleGoogle}
                onEmail={handleEmail}
                description={guestAuthDescription} // description
                source={guestAuthSource} // source
            />

            <div className="tw-flex tw-flex-col tw-h-full">
                <div
                    ref={scoreboardScrollContainerRef}
                    className="scoreboard-container tw-overflow-y-auto tw-overflow-x-hidden tw-flex-1"
                    style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }}
                >
                    <div>
                        <div className="tw-flex tw-gap-2 tw-mb-2 tw-bg-[transparent] tw-p-4 sm:tw-p-3 tw-flex-col tw-rounded-md">
                            <span className="tw-text-white tw-text-sm sm:tw-text-base tw-font-semibold">
                                Community Stats
                            </span>
                            {isLoadingStats ? (
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-gap-3 tw-rounded-md">
                                    <div className="tw-flex tw-flex-col tw-gap-1 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-rounded-md tw-animate-pulse">
                                        <div className="tw-h-3 tw-bg-[#3E3E3E] tw-rounded tw-w-24 tw-mb-2" />
                                        <div className="tw-h-6 tw-bg-[#3E3E3E] tw-rounded tw-w-32" />
                                        <div className="tw-h-3 tw-bg-[#3E3E3E] tw-rounded tw-w-20 tw-mt-2" />
                                        <div className="tw-h-6 tw-bg-[#3E3E3E] tw-rounded tw-w-16" />
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-gap-1 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-rounded-md tw-animate-pulse">
                                        <div className="tw-h-3 tw-bg-[#3E3E3E] tw-rounded tw-w-24 tw-mb-2" />
                                        <div className="tw-h-6 tw-bg-[#3E3E3E] tw-rounded tw-w-32" />
                                        <div className="tw-h-3 tw-bg-[#3E3E3E] tw-rounded tw-w-20 tw-mt-2" />
                                        <div className="tw-h-6 tw-bg-[#3E3E3E] tw-rounded tw-w-28" />
                                    </div>
                                </div>
                            ) : (
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-gap-3 tw-rounded-md">
                                    <div className="tw-flex tw-flex-col tw-gap-1 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-rounded-md">
                                        <div className="tw-text-white">
                                            <span className="tw-text-xs sm:tw-text-sm tw-text-gray-400">
                                                Average First Guess
                                            </span>
                                            <div
                                                className={clsx(
                                                    "tw-font-semibold tw-text-base sm:tw-text-xl tw-text-[#F69929]",
                                                    !isUserAuthenticated
                                                        ? "tw-blur-sm tw-cursor-pointer"
                                                        : !isPropertyGuessed && "tw-blur-sm tw-cursor-pointer",
                                                )}
                                                onClick={handleBlurredClick}
                                            >
                                                ${communityStats?.averageFirstGuess?.toLocaleString() ?? "0"}
                                            </div>
                                        </div>
                                        <div className="tw-text-white">
                                            <span className="tw-text-xs sm:tw-text-sm tw-text-gray-400">
                                                Average Score
                                            </span>
                                            <div
                                                className={clsx(
                                                    "tw-font-semibold tw-text-base sm:tw-text-xl tw-text-[#BB84FF]",
                                                    !isUserAuthenticated
                                                        ? "tw-blur-sm tw-cursor-pointer"
                                                        : !isPropertyGuessed && "tw-blur-sm tw-cursor-pointer",
                                                )}
                                                onClick={handleBlurredClick}
                                            >
                                                {(communityStats?.averageScore ?? 0).toFixed(1)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-flex-col tw-gap-1 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-rounded-md">
                                        <div className="tw-text-white">
                                            <span className="tw-text-xs sm:tw-text-sm tw-text-gray-400">
                                                Highest Guess
                                            </span>
                                            <div
                                                className={clsx(
                                                    "tw-font-semibold tw-text-base sm:tw-text-xl tw-text-white",
                                                    !isUserAuthenticated
                                                        ? "tw-blur-sm tw-cursor-pointer"
                                                        : !isPropertyGuessed && "tw-blur-sm tw-cursor-pointer",
                                                )}
                                                onClick={handleBlurredClick}
                                            >
                                                ${communityStats?.highestGuess?.toLocaleString() ?? "0"}
                                            </div>
                                        </div>
                                        <div className="tw-text-white">
                                            <span className="tw-text-xs sm:tw-text-sm tw-text-gray-400">
                                                Lowest Guess
                                            </span>
                                            <div
                                                className={clsx(
                                                    "tw-font-semibold tw-text-base sm:tw-text-xl tw-text-white",
                                                    !isUserAuthenticated
                                                        ? "tw-blur-sm tw-cursor-pointer"
                                                        : !isPropertyGuessed && "tw-blur-sm tw-cursor-pointer",
                                                )}
                                                onClick={handleBlurredClick}
                                            >
                                                ${communityStats?.lowestGuess?.toLocaleString() ?? "0"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <ScoreboardActionRow
                            isShareDisabled={!canShareScore}
                            isNextBusy={isNextPuzzleBusy}
                            onShareScore={handleShareScoreFromScoreboard}
                            onNextPuzzle={() => void handleNextPuzzleFromScoreboard()}
                        />

                        {/* Skeleton Loader for initial load */}
                        {isLoading && comments.length === 0 && renderSkeleton()}

                        {/* Error State - API fetch failed */}
                        {fetchError && !isLoading && (
                            <div className="tw-flex tw-gap-2 tw-mb-2 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-flex-col tw-rounded-md tw-items-center tw-justify-center">
                                <div className="tw-text-center tw-max-w-sm tw-p-2 sm:tw-p-3">
                                    <h3 className="tw-text-red-400 tw-text-lg sm:tw-text-xl tw-font-semibold tw-mb-2">
                                        Unable to Load Comments
                                    </h3>
                                    <p className="tw-text-gray-400 tw-text-sm sm:tw-text-base tw-mb-3">{fetchError}</p>
                                    <button
                                        onClick={() => void refetchComments()}
                                        className="tw-bg-violet-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg tw-font-medium hover:tw-bg-violet-700 tw-transition"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {isMigratingGuestScores && isUserAuthenticated && (
                            <div
                                role="status"
                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mx-4 tw-mb-2 tw-py-2 tw-px-3 tw-rounded-md tw-bg-[#232340] tw-text-[#ff8a3d] tw-text-xs"
                            >
                                <span className="tw-inline-block tw-w-3 tw-h-3 tw-border-2 tw-border-[#ff8a3d] tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                                Saving your scores…
                            </div>
                        )}

                        {/* Comments Section */}
                        <CommentsSection
                            puzzleCompleted={isPropertyGuessed || false}
                            comments={filteredComments}
                            totalGuesses={totalGuesses}
                            totalReplies={totalReplies}
                            actualPrice={actualPrice}
                            convertStatsToGuessCell={convertStatsToGuessCell}
                            isLoading={isLoading}
                            isUserAuthenticated={isUserAuthenticated}
                            currentUserId={
                                isUserAuthenticated
                                    ? (userState?.id ?? null)
                                    : null
                            }
                            currentUserNickname={
                                isUserAuthenticated
                                    ? userState?.nickname
                                    : null
                            }
                            onRequireSignInForProfileView={() =>
                                openGuestAuth(
                                    "Scoreboard", // location
                                    "profile_view", // source
                                    "Sign in to view profiles", // description
                                )
                            }
                            onRequireSignInForFollow={() =>
                                openGuestAuth(
                                    "Scoreboard", // location
                                    "follow", // source
                                    "Sign in to follow this agent and get updates", // description
                                )
                            }
                            onRequireSignInForComment={() =>
                                openGuestAuth(
                                    MIXPANEL_REGISTER_LOCATION_COMMENT_GATE, // location
                                    "comment", // source
                                    "Sign in to join the conversation", // description
                                )
                            }
                            onRequireSignInForCommentLike={() =>
                                openGuestAuth(
                                    MIXPANEL_REGISTER_LOCATION_LIKE_GATE_COMMENT, // location
                                    "like", // source
                                    COMMENT_LIKE_SIGN_IN_DESCRIPTION, // description
                                )
                            }
                            playablePropertyId={playablePropertyId}
                            userScoreRow={shouldPinUserScoreRow ? userScoreRow : null}
                            onSaveGuestScore={handleSaveGuestScoreClick}
                            onSubmitReplyApi={async (parentCommentId, content) => {
                                if (!playablePropertyId) {
                                    throw new Error("Missing playable property");
                                }
                                const userId = userState?.id;
                                if (!userId) {
                                    throw new Error("Missing user id");
                                }
                                const created = await createPlayablePropertyComment({
                                    playablePropertyId,
                                    content,
                                    userId,
                                    parentId: parentCommentId,
                                });
                                insertReplyLocally(parentCommentId, content, created?.id ? String(created.id) : undefined);
                                await syncCommentsAfterPost();
                            }}
                            onCommentsUpdate={() => {
                                void refetchComments();
                                invalidatePuzzleFeedCommentPreview();
                            }}
                            onFollowUpdate={(followedUserId, nextIsFollowing) => {
                                if (followedUserId && typeof nextIsFollowing === "boolean") {
                                    const targetUserId = String(followedUserId);
                                    updateCommentsCache((prev) => ({
                                        ...prev,
                                        data: applyFollowStateToCommentTree(prev.data || [], targetUserId, nextIsFollowing),
                                    }));
                                }
                                void refetchComments();
                                invalidatePuzzleFeedCommentPreview();
                            }}
                            scoreboardScrollContainerRef={scoreboardScrollContainerRef}
                        />

                        {/* Empty State - No data available */}
                        {!isLoading && !fetchError && comments.length === 0 && playablePropertyId && (
                            <div className="tw-flex tw-gap-2 tw-mb-2 tw-bg-[#232340] tw-p-2 sm:tw-p-3 tw-flex-col tw-rounded-md tw-items-center tw-justify-center">
                                <div className="tw-text-center tw-max-w-sm tw-p-2 sm:tw-p-3">
                                    <h3 className="tw-text-white tw-text-lg sm:tw-text-xl tw-font-semibold tw-mb-2">
                                        No Comments Yet
                                    </h3>
                                    <p className="tw-text-gray-400 tw-text-sm sm:tw-text-base">
                                        Be the first to guess this property and share your thoughts!
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <StickyCommentInputBar
                    playablePropertyId={playablePropertyId}
                    solvedScore={isPropertyGuessed ? (score ?? null) : null}
                    scoreBadgeGuesses={scoreBadgeGuesses}
                    onRequireSignIn={() => {
                        openGuestAuth(
                            MIXPANEL_REGISTER_LOCATION_COMMENT_GATE, // location
                            "comment", // source
                            "Sign in to join the conversation", // description
                        );
                    }}
                    onSubmitComment={async (content) => {
                        if (!playablePropertyId) {
                            throw new Error("Missing playable property");
                        }
                        const userId = userState?.id;
                        if (!userId) {
                            throw new Error("Missing user id");
                        }

                        const created = await createPlayablePropertyComment({
                            playablePropertyId,
                            content,
                            userId,
                        });
                        insertTopLevelCommentLocally(content, created?.id ? String(created.id) : undefined);
                        await syncCommentsAfterPost();
                    }}
                />
            </div>
        </div>
    );
}
